'use strict';

/**
 * Filter & Sort modal. Pending selections live in the modal DOM and commit to the grid only
 * on "View results"; closing without applying restores the committed state.
 */

// Via drawer.js so the scroll lock it owns is released.
const closeDrawer = require('../components/drawer').closeDrawer;

const DRAWER = '#filter-sort-drawer';
const MAX_VISIBLE_TAGS = 3;

/**
 * Groups checked refinement values by attribute id, joining multiple values of the same
 * attribute with "|" (SFRA OR-within-attribute convention).
 * @param {jQuery} $drawer - the modal root
 * @returns {Object} map of attributeId -> "val1|val2"
 */
function collectPreferences($drawer) {
    const prefs = {};
    $drawer.find('.lvz-filter__checkbox:checked').each(function () {
        const attr = $(this).data('attr');
        const value = $(this).data('value');
        if (!attr) return;
        prefs[attr] = prefs[attr] ? prefs[attr] + '|' + value : String(value);
    });
    return prefs;
}

/**
 * Builds the query params common to Search-Count and Search-ShowAjax from the pending state.
 * @param {jQuery} $drawer - the modal root
 * @returns {Object} jQuery-ajax `data` object
 */
function buildParams($drawer) {
    const params = {};
    const cgid = $drawer.data('cgid');
    const q = $drawer.data('q');
    if (cgid) params.cgid = cgid;
    if (q) params.q = q;

    const srule = $drawer.find('input[name="plp-sort"]:checked').val();
    if (srule) params.srule = srule;

    const prefs = collectPreferences($drawer);
    let i = 1;
    Object.keys(prefs).forEach(function (attr) {
        params['prefn' + i] = attr;
        params['prefv' + i] = prefs[attr];
        i += 1;
    });
    return params;
}

let countTimer = null;
let countXhr = null;
let countSeq = 0;

/**
 * Recomputes the live result count and refreshes the "View results (n)" CTA and group badges.
 * Badges update immediately; the server count is debounced so a burst of toggles issues a single
 * request, and the in-flight request is aborted so a stale response can never overwrite a newer one.
 * @param {jQuery} $drawer - the modal root
 */
function refreshCount($drawer) {
    const $apply = $drawer.find('[data-lvz-filter-apply]');
    const template = $apply.data('label-template');
    const selectedCountLabel = $drawer.attr('data-selected-count-label') || '';

    $drawer.find('.lvz-filter__value, .lvz-filter__toggle, [data-lvz-filter-group]').prop('hidden', false);

    $drawer.find('.lvz-filter__group-item').each(function () {
        const $checked = $(this).find('.lvz-filter__checkbox:checked');
        const count = $checked.length;
        $(this).find('[data-lvz-group-badge]')
            .text(count)
            .attr('aria-label', selectedCountLabel.replace('{0}', count))
            .toggleClass('d-none', count === 0);
        const labels = $checked.map(function () {
            return $(this).closest('label').find('.checkbox__label').text().trim();
        }).get().join(', ');
        $(this).find('[data-lvz-group-selected]').text(labels).prop('hidden', count === 0);
    });

    clearTimeout(countTimer);
    countTimer = setTimeout(function () {
        if (countXhr) { countXhr.abort(); }
        const seq = ++countSeq;
        countXhr = $.ajax({
            url: $drawer.data('count-url'),
            method: 'GET',
            data: buildParams($drawer),
            success: function (response) {
                if (seq !== countSeq) { return; }
                const count = response && typeof response.count === 'number' ? response.count : '';
                $apply.text(template.replace('{0}', count));
                applyAvailability($drawer, response && response.available);
            }
        });
    }, 250);
}

/**
 * Hides the refinement values that match no product under the pending selection, and shows them
 * again when a later change makes them available. Checked values always stay visible so they can
 * be removed; a group left with nothing visible hides too.
 *
 * @param {jQuery} $drawer - the modal root
 * @param {Object} available - attributeId -> array of still-selectable values
 */
function applyAvailability($drawer, available) {
    if (!available) return;

    $drawer.find('.lvz-filter__checkbox').each(function () {
        const $box = $(this);
        const list = available[String($box.data('attr'))] || [];
        const isAvailable = $box.prop('checked') ||
            list.indexOf(String($box.data('value'))) !== -1;
        $box.closest('.lvz-filter__value, .lvz-filter__toggle').prop('hidden', !isAvailable);
    });

    $drawer.find('[data-lvz-filter-group]').each(function () {
        const $group = $(this);
        $group.prop('hidden', $group.find('.lvz-filter__value:not([hidden])').length === 0);
    });
}

/**
 * Commits the pending selection to the grid and closes the modal.
 * @param {jQuery} $drawer - the modal root
 * @param {Object} [options]
 * @param {Object} [options.trackingHint] - single-filter quick-toggle hint for GTM
 */
function applyFilters($drawer, options) {
    options = options || {};
    const params = buildParams($drawer);
    $.spinner().start();
    $.ajax({
        lvzSkipAutoSpinner: true,
        url: $drawer.data('apply-url'),
        method: 'GET',
        data: params,
        success: function (response) {
            const $r = $('<div>').append($(response));

            if (window.lavazzaGtm && window.lavazzaGtm.plp) {
                window.lavazzaGtm.plp.trackFilterCommit({
                    drawer: $drawer,
                    trackingHint: options.trackingHint || null
                });
            }

            $('.product-grid').empty().html($r.find('.product-grid').html());
            $('[data-lvz-search-item-count]').replaceWith($r.find('[data-lvz-search-item-count]'));
            $('[data-lvz-filter-refinements]').html($r.find('[data-lvz-filter-refinements]').html());
            $('.lvz-quick-filters').replaceWith($r.find('.lvz-quick-filters'));

            $('body').trigger('search:updateDom', '.product-grid');

            const showUrl = $drawer.data('show-url') + '?' + $.param(params);
            window.history.pushState({}, null, showUrl);

            snapshot($drawer);

            closeDrawer($drawer);
        },
        // In complete, not success: a throw above would leave the body scroll-locked.
        complete: function () {
            $.spinner().stop();
        }
    });
}

/**
 * Snapshots the committed checkbox/radio state so closing without applying can restore it.
 * @param {jQuery} $drawer - the modal root
 */
function snapshot($drawer) {
    $drawer.find('.lvz-filter__checkbox, input[name="plp-sort"]').each(function () {
        $(this).data('committed', $(this).prop('checked'));
    });
}

/**
 * Restores checkbox/radio state to the last committed snapshot.
 * @param {jQuery} $drawer - the modal root
 */
function restore($drawer) {
    $drawer.find('.lvz-filter__checkbox, input[name="plp-sort"]').each(function () {
        const committed = $(this).data('committed');
        if (committed !== undefined) {
            $(this).prop('checked', committed);
        }
    });
    resetOrder($drawer);
    renderTags($drawer);
    refreshCount($drawer);
}

// Selection order (keys "attr::value"), oldest first — tags render it reversed (newest first).
let selectionOrder = [];
let tagsExpanded = false;

/**
 * @param {jQuery} $box - a refinement checkbox
 * @returns {string} stable key for the selection
 */
function keyOf($box) {
    return $box.data('attr') + '::' + $box.data('value');
}

/**
 * Rebuilds the selection order from the currently-checked boxes (used on open/restore).
 * @param {jQuery} $drawer - the modal root
 */
function resetOrder($drawer) {
    selectionOrder = [];
    $drawer.find('.lvz-filter__checkbox:checked').each(function () {
        selectionOrder.push(keyOf($(this)));
    });
}

/**
 * Renders the selected-filter tags: newest first, max 3 visible with a See all / See less toggle.
 * @param {jQuery} $drawer - the modal root
 */
function renderTags($drawer) {
    const $container = $drawer.find('[data-lvz-filter-tags]').empty();

    // Switches opt out: they already show their own state.
    const boxes = $drawer.find('.lvz-filter__checkbox:checked').not('[data-lvz-no-tag]').get();
    if (!boxes.length) {
        $container.prop('hidden', true);
        return;
    }
    $container.prop('hidden', false);

    const byAttr = {};
    const groups = [];
    boxes.forEach(function (box) {
        const $box = $(box);
        const attr = String($box.data('attr'));
        const label = $box.closest('label').find('.checkbox__label').text().trim();
        const order = selectionOrder.indexOf(keyOf($box));
        if (!byAttr[attr]) {
            byAttr[attr] = {
                attr: attr,
                groupName: $box.closest('.lvz-filter__group-item').find('.lvz-filter__group-name').text().trim(),
                labels: [],
                order: -1
            };
            groups.push(byAttr[attr]);
        }
        byAttr[attr].labels.push(label);
        if (order > byAttr[attr].order) byAttr[attr].order = order;
    });

    groups.sort(function (a, b) {
        return (b.order < 0 ? Infinity : b.order) - (a.order < 0 ? Infinity : a.order);
    });

    const removeGroupLabel = $drawer.attr('data-remove-group-label') || '';

    groups.forEach(function (group, i) {
        const values = group.labels.join(', ');
        const hidden = !tagsExpanded && i >= MAX_VISIBLE_TAGS;

        $('<span>')
            .attr('class', 'tag tag--secondary lvz-filter__tag')
            .attr('aria-label', group.groupName + ': ' + values)
            .toggleClass('d-none', hidden)
            .html(group.groupName + ': <strong>' + values + '</strong>')
            .append(
                $('<button>').attr({
                    type: 'button',
                    'class': 'lvz-filter__tag-remove',
                    'data-lvz-tag-remove': group.attr,
                    'aria-label': removeGroupLabel.replace('{0}', group.groupName)
                }).html('&times;')
            )
            .appendTo($container);
    });

    if (groups.length > MAX_VISIBLE_TAGS) {
        $('<button>')
            .attr({
                type: 'button',
                'class': 'lvz-filter__tags-toggle',
                'data-lvz-tags-toggle': '',
                'aria-expanded': tagsExpanded ? 'true' : 'false'
            })
            .text(tagsExpanded
                ? $container.data('less-label')
                : $container.data('all-label'))
            .append('<i class="icon-arrow-angle-down lvz-filter__tags-toggle-icon" aria-hidden="true"></i>')
            .appendTo($container);
    }
}

module.exports = function () {
    // Seed committed flags from the server-rendered state so diffs are valid even before first open.
    snapshot($(DRAWER));

    // Snapshot the committed state each time the modal opens, so a discard restores it.
    $(document).on('click', '[data-drawer-open="filter-sort-drawer"]', function () {
        const $drawer = $(DRAWER);
        snapshot($drawer);
        resetOrder($drawer);
        tagsExpanded = false;
        renderTags($drawer);
        refreshCount($drawer);
    });

    $(document).on('change', DRAWER + ' .lvz-filter__checkbox', function () {
        const key = keyOf($(this));
        const pos = selectionOrder.indexOf(key);
        if ($(this).prop('checked') && pos === -1) {
            selectionOrder.push(key);
        } else if (!$(this).prop('checked') && pos !== -1) {
            selectionOrder.splice(pos, 1);
        }
        renderTags($(DRAWER));
    });

    $(document).on('change', DRAWER + ' .lvz-filter__checkbox, ' + DRAWER + ' input[name="plp-sort"]', function () {
        refreshCount($(DRAWER));
    });

    $(document).on('click', '[data-lvz-tag-remove]', function () {
        const attr = String($(this).data('lvz-tag-remove'));
        const $drawer = $(DRAWER);
        $drawer.find('.lvz-filter__checkbox').filter(function () {
            return String($(this).data('attr')) === attr;
        }).each(function () {
            $(this).prop('checked', false);
            const pos = selectionOrder.indexOf(keyOf($(this)));
            if (pos !== -1) selectionOrder.splice(pos, 1);
        });
        renderTags($drawer);
        refreshCount($drawer);
    });

    $(document).on('click', '[data-lvz-tags-toggle]', function () {
        tagsExpanded = !tagsExpanded;
        renderTags($(DRAWER));
    });

    $(document).on('click', '[data-lvz-filter-apply]', function () {
        applyFilters($(this).closest(DRAWER));
    });

    // Rapid toggles in the quick-filters bar apply their change through the same pipeline.
    $('body').on('lvz:applyFilters', function (_e, data) {
        applyFilters($(DRAWER), data || {});
    });

    $(document).on('click', '[data-lvz-filter-clear]', function () {
        const $drawer = $(this).closest(DRAWER);
        $drawer.find('.lvz-filter__checkbox').prop('checked', false);
        selectionOrder = [];
        renderTags($drawer);
        refreshCount($drawer);
    });

    // Closing via overlay / X discards pending selections.
    $(document).on('click', DRAWER + ' .drawer__overlay, ' + DRAWER + ' [data-drawer-close]', function () {
        restore($(DRAWER));
    });

    // Esc also closes the drawer (handled by drawer.js) — discard here too.
    $(document).on('keydown', function (e) {
        if (e.key === 'Escape' && $(DRAWER).hasClass('is-open')) {
            restore($(DRAWER));
        }
    });
};
